'use client';

import { cva, type VariantProps } from 'class-variance-authority';
import { motion, MotionValue, useMotionValue, useSpring, useTransform } from 'framer-motion';
import React, { PropsWithChildren, useRef } from 'react';
import { cn } from '@/lib/utils';
export interface DockProps extends VariantProps<typeof dockVariants> {
  className?: string;
  magnification?: number;
  distance?: number;
  direction?: 'top' | 'middle' | 'bottom';
  children: React.ReactNode;
}
const DEFAULT_MAGNIFICATION = 60;
const DEFAULT_DISTANCE = 0;
const dockVariants = cva('supports-backdrop-blur:bg-white/10 mx-auto flex w-max rounded-[14px] border px-6 py-0 h-[74px] backdrop-blur-md border-[0.5px] border-[solid] border-[#FFFFFF33] bg-[rgba(255,255,255,0.55)] mobile-vertical-menu');
const Dock = React.forwardRef<HTMLDivElement, DockProps>(({
  className,
  children,
  magnification = DEFAULT_MAGNIFICATION,
  distance = DEFAULT_DISTANCE,
  direction = 'bottom',
  ...props
}, ref) => {
  const mouseX = useMotionValue(Infinity);
  const renderChildren = () => {
    return React.Children.map(children, child => {
      if (React.isValidElement(child) && child.type === DockIcon) {
        return React.cloneElement(child, {
          ...child.props,
          mouseX,
          magnification,
          distance
        });
      }
      return child;
    });
  };
  return <motion.div ref={ref} onMouseMove={e => mouseX.set(e.pageX)} onMouseLeave={() => mouseX.set(Infinity)} {...props} className={cn(dockVariants({
    className
  }), {
    'items-start': direction === 'top',
    'items-center': direction === 'middle',
    'items-end': direction === 'bottom'
  })}>
        {renderChildren()}
      </motion.div>;
});
Dock.displayName = 'Dock';
export interface DockIconProps {
  size?: number;
  magnification?: number;
  distance?: number;
  mouseX?: MotionValue<number>;
  className?: string;
  children?: React.ReactNode;
  props?: PropsWithChildren;
}
const DockIcon = ({
  magnification = DEFAULT_MAGNIFICATION,
  distance = DEFAULT_DISTANCE,
  mouseX,
  className,
  children,
  ...props
}: DockIconProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const fallbackMotionValue = useMotionValue(0);
  const distanceCalc = useTransform(mouseX ?? fallbackMotionValue, (val: number) => {
    const bounds = ref.current?.getBoundingClientRect() ?? {
      x: 0,
      width: 0
    };
    return val - bounds.x - bounds.width / 2;
  });
  const widthSync = useTransform(distanceCalc, [-distance, 0, distance], [75, magnification, 52]);
  const width = useSpring(widthSync, {
    mass: 0.1,
    stiffness: 150,
    damping: 12
  });
  return <motion.div ref={ref} style={{
    width
  }} className={cn('relative mt-0.5 flex cursor-pointer items-center justify-center rounded-full', className)} {...props} data-sentry-element="unknown" data-sentry-component="DockIcon" data-sentry-source-file="dock.tsx">
      {children}
    </motion.div>;
};
DockIcon.displayName = 'DockIcon';
export { Dock, DockIcon, dockVariants };